import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { DataSource, DataSourceIDEnum, EDataSourceCategory } from '@/models/dataSource';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DataSourceDeleteButton, DataSourceDownloadButton, DataSourceStatusBadge } from '@/features/dataSources/components/commons';
import { format } from 'date-fns';
import { DataSourceDetailEditableName } from './DataSourceDetailEditableName';
import { DataSourceResyncButton } from '@/features/dataSources/components/commons/DataSourceResyncButton';

type DataSourceDetailHeaderProps = {
  dataSource: DataSource;
  isActive: boolean;
};

export const DataSourceDetailHeader: FC<DataSourceDetailHeaderProps> = ({ dataSource, isActive }) => {
  const { t } = useTranslation(['COMMON', 'ALL', 'data_source_details']);

  const isWorkspaceAdmin = useWorkspaceAdmin();

  return (
    <header className='flex justify-between bg-white-100 px-8 py-4'>
      <div className='flex max-w-[50%] flex-col'>
        <div className='flex items-center gap-4'>
          <DataSourceDetailEditableName dataSource={dataSource} />
          <DataSourceStatusBadge status={dataSource?.status} />
        </div>
        <div className='flex items-end gap-6 text-tertiary-600'>
          <p className='flex gap-1'>
            <span className='font-semibold text-primary-900'>{t('COMMON:id')}:</span> {dataSource?.id}
          </p>
          <p className='flex gap-1 text-sm'>
            {t('data_source_details:overview.installed_on')}:
            <span className='font-semibold text-secondary-700'>
              {dataSource.activatedOn ? format(new Date(dataSource.activatedOn), 'yyyy-MM-dd HH:mm:ss') : '-'}
            </span>
          </p>
        </div>
      </div>

      <div className='flex items-center gap-6'>
        {isActive && (dataSource?.type?.id === DataSourceIDEnum.SFTP || dataSource?.type?.id === DataSourceIDEnum.AmazonS3) && (
          <DataSourceResyncButton buttonText='COMMON:sync' dataSource={dataSource} />
        )}

        {isActive && dataSource?.type.category === EDataSourceCategory.FILE && (
          <DataSourceDownloadButton buttonText='COMMON:download' datasource={dataSource} />
        )}

        {isWorkspaceAdmin && <DataSourceDeleteButton buttonText='COMMON:delete' datasource={dataSource} showTooltip />}
      </div>
    </header>
  );
};
